import React from 'react'
import PropTypes from 'prop-types'
import { useTextContext } from 'hocs'
import { Switch } from 'components/atoms'
import * as Styled from './FilterOverlayContent.styles'
import { IndicatorFilter } from 'components/molecules/Filter/partials'
import { IndicatorLegend } from 'components/molecules/NursingIndicator/partials'

/**
 * filter mapper for modal
 * with internal state handling
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const FilterOverlayContent = (props) => {
  const textContext = useTextContext()
  const { filter = {}, onChange, variant = 'circle', desc } = props

  /**
   * internal state
   * @param key
   * @param newRating
   */
  const handleUpdateFilter = (key, newRating) => {
    try {
      if (!key) {
        throw `Error in handleUpdateFilter, key not provided.`
      }

      const updatedFilterOptions = filter.options.map((option) => {
        if (option.options && option.options.length > 0) {
          return {
            ...option,
            options: option.options.map((indicatorOption) => {
              if (indicatorOption.key === key) {
                return {
                  ...indicatorOption,
                  selected: newRating,
                }
              }

              return indicatorOption
            }),
          }
        }

        if (option.key === key) {
          return {
            ...option,
            selected: newRating,
          }
        }

        return option
      })

      onChange?.({ ...filter, options: updatedFilterOptions })
    } catch (e) {
      console.log(`Error in handleUpdateFilter error message: ${ e }`)
    }
  }

  /**
   * get user selected filters for filter information in header area
   * @returns {string|boolean}
   */
  const getSelectedFilter = () => {
    if (!filter.options) return true

    const selectedFilter = []

    filter.options.forEach((option, index) => {
      if (option.options && option.options.length > 0) {
        option.options.forEach((indicatorOption, indicatorIndex) => {
          if (indicatorOption.selected) {
            if (option.options.length === 1) {
              selectedFilter.push(`${ index + 1 }`)
              return
            }
            selectedFilter.push(
              `${ index + 1 }.${
                variant === 'circle'
                  ? String.fromCharCode(97 + indicatorIndex)
                  : indicatorIndex + 1
              }`
            )
          }
        })
        return
      }

      if (option.selected) {
        selectedFilter.push(`${ index + 1 }`)
      }
    })

    return selectedFilter
  }

  return (
    <Styled.FilterOverlayContent>
      {/* filter header with legend and selected filters */}
      {getSelectedFilter().length > 0 && (
        <p className='selected-filter'>
          {textContext['nursingIndicator.filter.selectedFilter.prefix']}
          {getSelectedFilter().join(', ')}
        </p>
      )}
      <p>
        {desc ?? textContext[`nursingIndicator.filter.description.${ variant }`]}
      </p>
      <IndicatorLegend withDescription variant={ variant } />
      <hr />

      {/* filter mapping */}
      {filter.options &&
        filter.options.map((option, index) => (
          <React.Fragment key={ `accordion-${ variant }-${ index }` }>
            <Styled.Accordion
              label={
                <div className='accordion-title'>
                  <span>{index + 1}.</span>
                  <span>{option.label}</span>
                </div>
              }
              open={ index === 0 }>
              <div className='indicator-description'>
                <p>{option.desc}</p>
                {option.options && option.options.length === 1 && (
                  <div className='indicator'>
                    <IndicatorFilter
                      variant={ variant }
                      rating={ option.options[0].selected }
                      onChange={ (newRating) =>
                        handleUpdateFilter(option.options[0].key, newRating)
                      }
                    />
                  </div>
                )}
              </div>

              {/* mapping der einzelnen indikatoren */}
              {option.options &&
                option.options.length > 1 &&
                option.options.map((indicatorOption, indicatorIndex) => (
                  <div
                    className='indicator-row'
                    key={ `indicator-row-${ indicatorIndex }` }>
                    <b className='indicator-enumeration'>
                      {variant === 'circle'
                        ? String.fromCharCode(97 + indicatorIndex)
                        : `${ index + 1 }.${ indicatorIndex + 1 }`}
                      .
                    </b>

                    <div className='indicator'>
                      <p>{indicatorOption.label}</p>

                      {indicatorOption.type === 'switch' ? (
                        <Switch
                          checked={ indicatorOption.selected }
                          onChange={ () =>
                            handleUpdateFilter(
                              indicatorOption.key,
                              !indicatorOption.selected
                            )
                          }
                        />
                      ) : (
                        <IndicatorFilter
                          variant={ variant }
                          rating={ indicatorOption.selected }
                          onChange={ (newRating) =>
                            handleUpdateFilter(indicatorOption.key, newRating)
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
            </Styled.Accordion>
            <hr />
          </React.Fragment>
        ))}
    </Styled.FilterOverlayContent>
  )
}

FilterOverlayContent.propTypes = {
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['circle', 'square']),
  desc: PropTypes.string,
  filter: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    selected: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.oneOf(['switch', 'select', 'group']),
        selected: PropTypes.oneOfType([
          PropTypes.bool,
          PropTypes.number,
          PropTypes.string,
        ]),
        options: PropTypes.arrayOf(
          PropTypes.shape({
            chip_label: PropTypes.string,
            isDuplicate: null,
            key: PropTypes.string,
            label: PropTypes.string,
            selected: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
            tip: PropTypes.string,
            type: PropTypes.oneOf(['value', 'switch']),
          })
        ),
      })
    ),
  }),
}

export default FilterOverlayContent
